import React from "react";
import { clsx } from "clsx";

import { PreviewErrorCard } from "~components/shared/card/Card";
import Headline from "~components/shared/headline/Headline";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    DEFAULT_MEDIA_PRIORITY,
    sourceHasValidMedia,
    AuthorImageMedia,
} from "~components/shared/media";
import OptionalEyebrow from "~components/shared/optional-eyebrow/OptionalEyebrow";
import PhoenixByline from "~components/shared/phoenix-byline/PhoenixByline";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import TertiaryItems from "~components/shared/tertiary-items/TertiaryItems";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import WeekendHeader from "~components/shared/weekend-header/WeekendHeader";
import { HEADLINE_SIZE } from "~types/lineup";
import {
    type VariationComponent,
    ZoneColumnCombo,
    isTextOverMediaItemVariation,
    type CuratedStory,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentWeekendLandscape.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentWeekendLandscape.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];
export const CURATION_WARNING =
    "This module requires at least 4 stories to render properly. Please add more stories.";

// Media consts shared across both Portrait and Landscape versions
// of the weekend module.
export const SECONDARY_ITEM_MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.loopingVideo,
    MediaFormat.videoImage,
    MediaFormat.defaultImage,
];
export const SECONDARY_ITEM_MEDIA_CONFIG: MediaConfig = {
    desktopMaxWidth: 300,
    mobileMaxWidth: 740,
    aspectRatio: 3 / 2,
    showCaption: false,
};

// Weekend Landscape supports all default media priorities unless
// the overlay treatment is applied by editors. We pre-define the
// Media Priority in each case.
export const LEDE_ITEM_MEDIA_PRIORITY: MediaFormat[] = DEFAULT_MEDIA_PRIORITY;
export const LEDE_ITEM_OVERLAY_MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.loopingVideo,
    MediaFormat.defaultImage,
];
const LEDE_ITEM_MEDIA_CONFIG: MediaConfig = {
    desktopMaxWidth: 830,
    mobileMaxWidth: 740,
    aspectRatio: 3 / 2,
    showCaption: true,
    className: styles.ledeItemMedia,
};

export default function LineupContentWeekendLandscape(
    props: VariationComponent
) {
    const { items = [], labelUrl } = props;

    const integrity = integrityCheck({ ...props });
    if (integrity.length) return integrityError(integrity);

    if (!items?.length) return null;
    const [ledeItem, ...supportingItems] = items;

    const { franchise, updatedAt, publishedAt } =
        "franchise" in ledeItem ? (ledeItem as CuratedStory) : {};

    const useTextOverMedia = isTextOverMediaItemVariation(ledeItem.variation);

    // We need to limit the available Media Formats if editors
    // have applied either of the text-over-image treatments because
    // users are unable to interact with Media when we overlay text.
    // Embedded i-frames, Videos-on-demand, Livestream players and Snapshot Charts
    // cannot be used, for example.
    const ledeItemMediaPriority = useTextOverMedia
        ? LEDE_ITEM_OVERLAY_MEDIA_PRIORITY
        : LEDE_ITEM_MEDIA_PRIORITY;

    return (
        <VariantBase
            source={props}
            className={styles.LineupContentWeekendLandscape}
            gridClassName={styles.grid}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <WeekendHeader titleUrl={labelUrl || "/weekend"} />
            <div className={styles.ledeAndSecondaryItems}>
                <div
                    className={clsx([
                        styles.ledeItem,
                        useTextOverMedia && styles.textOverImage,
                    ])}
                >
                    <Media
                        source={ledeItem}
                        priority={ledeItemMediaPriority}
                        config={LEDE_ITEM_MEDIA_CONFIG}
                    />
                    <a
                        href={ledeItem.url}
                        className={styles.storyLink}
                        data-component="story-link"
                        data-link-index={1}
                    >
                        <div
                            className={styles.storyBlock}
                            data-component="story-block"
                        >
                            <OptionalEyebrow
                                label={ledeItem.label}
                                franchise={franchise}
                                updatedAt={updatedAt}
                                publishedAt={publishedAt}
                                textLabel={ledeItem.textLabel}
                                presetLabel={ledeItem.labelDropdown}
                                nicodes={ledeItem.nicodes}
                                className={styles.optionalEyebrow}
                            />
                            <Headline
                                text={ledeItem.headline}
                                isPhoenix
                                className={styles.headline}
                                size={HEADLINE_SIZE.UNSTYLED}
                            />
                            <div className={styles.authorImageAndByline}>
                                <AuthorImageMedia
                                    authorImage={ledeItem?.media?.authorImage}
                                    config={
                                        {
                                            desktopMaxWidth: 62,
                                            mobileMaxWidth: 62,
                                            aspectRatio: 1,
                                            className: styles.authorImage,
                                        } as MediaConfig
                                    }
                                />
                                <PhoenixByline
                                    forceShowByline
                                    story={ledeItem}
                                />
                            </div>
                        </div>
                    </a>
                </div>
                <div className={styles.secondaryItems}>
                    {supportingItems.length > 0 &&
                        supportingItems.slice(0, 3).map((item, index) => (
                            <div className={styles.item} key={item.url}>
                                <Media
                                    source={item}
                                    priority={SECONDARY_ITEM_MEDIA_PRIORITY}
                                    config={SECONDARY_ITEM_MEDIA_CONFIG}
                                />
                                <StoryBlock
                                    story={item}
                                    withOptionalEyebrow
                                    isSecondaryStory
                                    isExtraSmallHeadline
                                    storyIndex={index + 2}
                                    disableFeaturedStyling
                                    withDek={
                                        !sourceHasValidMedia(
                                            item,
                                            SECONDARY_ITEM_MEDIA_PRIORITY
                                        )
                                    }
                                />
                            </div>
                        ))}
                    {supportingItems.length < 3 && (
                        <PreviewErrorCard message="This module requires at least 4 stories to render properly. Please add more stories." />
                    )}
                </div>
            </div>
            {supportingItems.length > 3 && (
                <TertiaryItems items={supportingItems.slice(3, 6)} />
            )}
        </VariantBase>
    );
}
